import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { LuCopy, LuEye, LuEyeOff, LuRefreshCw } from "react-icons/lu";
import API from "../../api";
import { capture } from "../../instrument";

const KEY_TYPES = [
  { type: "apikey", label: "Clé primaire" },
  { type: "apikey2", label: "Clé secondaire" },
];

const ApiKeysSection = () => {
  const queryClient = useQueryClient();
  const [revealedKeys, setRevealedKeys] = useState({});

  const { data: keys, isError } = useQuery({
    queryKey: ["apikeys"],
    queryFn: async () => {
      const { ok, data, message } = await API.get("/apikey");
      if (!ok) throw new Error(message);
      return data;
    },
  });

  const refreshMutation = useMutation({
    mutationFn: ({ id, type }) => API.post(`/apikey/${id}/refresh/${type}`),
    onSuccess: () => {
      toast.success("La clé a été réinitialisée");
      queryClient.invalidateQueries({ queryKey: ["apikeys"] });
    },
    onError: (error) => {
      capture(error);
      toast.error(error.message || "Impossible de réinitialiser la clé");
    },
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, disabled }) => API.post(`/apikey/${id}/disable`, { disabled }),
    onSuccess: (response, { disabled }) => {
      toast.success(disabled ? "Les clés ont été désactivées" : "Les clés ont été activées");
      queryClient.invalidateQueries({ queryKey: ["apikeys"] });
    },
    onError: (error) => {
      capture(error);
      toast.error(error.message || "Impossible de modifier le statut des clés");
    },
  });

  const onRefreshKey = (key, type) => {
    if (!window.confirm(`Voulez-vous vraiment réinitialiser la clé "${key[type]}" ?`)) return;
    refreshMutation.mutate({ id: key._id, type });
  };

  const onToggleKey = (key) => {
    if (!window.confirm(`Voulez-vous vraiment ${key.disabled ? "activer" : "désactiver"} ces clés d'API ?`)) return;
    statusMutation.mutate({ id: key._id, disabled: !key.disabled });
  };

  const onCopyKey = (value, label) => {
    navigator.clipboard.writeText(value);
    toast.success(`${label} copiée`);
  };

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-2xl font-semibold tracking-tight text-gray-900">Clés d'API</h2>
        <p className="mt-1.5 text-sm text-gray-500">Clés utilisées par vos intégrations pour appeler l'API Querco.</p>
      </div>

      {isError && <p className="text-sm text-gray-500">Impossible de charger les clés d'API.</p>}

      {!keys && !isError && (
        <div className="flex justify-center py-16">
          <div className="size-5 animate-spin rounded-full border-2 border-gray-300 border-t-primary-500" />
        </div>
      )}

      {keys?.length === 0 && <p className="rounded-xl border border-dashed border-gray-200 px-4 py-12 text-center text-sm text-gray-500">Aucune clé d'API</p>}

      <div className="flex flex-col gap-4">
        {keys?.map((key) => (
          <div key={key._id} className="divide-y divide-gray-100 rounded-xl bg-white px-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)] ring-1 ring-gray-950/[0.06]">
            <div className="flex items-center justify-between gap-6 py-3.5">
              <div className="min-w-0">
                <p className="text-[13px] font-medium text-gray-900">{key.scopes.length > 0 ? "Clé agent de synchronisation" : "Paire de clés"}</p>
                <p className="mt-0.5 flex items-center gap-1.5 text-[13px] text-gray-500">
                  <span className={`size-1.5 rounded-full ${key.disabled ? "bg-gray-300" : "bg-green-500"}`} />
                  {key.disabled ? "Désactivée, les appels signés avec ces clés sont refusés" : "Active"}
                </p>
              </div>
              <span className="relative inline-flex h-5 w-9 shrink-0 rounded-full bg-gray-200 p-0.5 transition-colors has-[:checked]:bg-primary-500 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-primary-500 has-[:focus-visible]:ring-offset-2">
                <input
                  type="checkbox"
                  role="switch"
                  name="keyPairEnabled"
                  aria-label="Paire de clés active"
                  className="peer absolute inset-0 size-full cursor-pointer appearance-none rounded-full focus:outline-none"
                  checked={!key.disabled}
                  onChange={() => onToggleKey(key)}
                />
                <span className="pointer-events-none size-4 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-4" />
              </span>
            </div>
            {KEY_TYPES.map(({ type, label }) => {
              const isRevealed = revealedKeys[`${key._id}.${type}`];
              return (
                <div key={type} className={`flex items-center gap-4 py-2.5 ${key.disabled ? "pointer-events-none opacity-50" : ""}`}>
                  <p className="w-32 shrink-0 text-[13px] font-medium text-gray-900">{label}</p>
                  <p className="min-w-0 flex-1 truncate font-mono text-xs text-gray-600">
                    {isRevealed ? key[type] : `${key[type].slice(0, 8)}${"•".repeat(20)}${key[type].slice(-4)}`}
                  </p>
                  <div className="flex shrink-0 items-center gap-0.5">
                    <button
                      type="button"
                      onClick={() => setRevealedKeys({ ...revealedKeys, [`${key._id}.${type}`]: !isRevealed })}
                      title={isRevealed ? "Masquer" : "Afficher"}
                      aria-label={`${isRevealed ? "Masquer" : "Afficher"} la ${label.toLowerCase()}`}
                      className="grid size-7 place-items-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
                    >
                      {isRevealed ? <LuEyeOff className="size-4" /> : <LuEye className="size-4" />}
                    </button>
                    <button
                      type="button"
                      onClick={() => onCopyKey(key[type], label)}
                      title="Copier"
                      aria-label={`Copier la ${label.toLowerCase()}`}
                      className="grid size-7 place-items-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
                    >
                      <LuCopy className="size-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => onRefreshKey(key, type)}
                      title="Réinitialiser"
                      aria-label={`Réinitialiser la ${label.toLowerCase()}`}
                      className="grid size-7 place-items-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
                    >
                      <LuRefreshCw className="size-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {keys?.length > 0 && (
        <p className="mt-2.5 px-4 text-xs text-gray-500">
          Réinitialiser une clé désactive immédiatement l'ancienne. Les applications qui l'utilisent perdent l'accès tant que la nouvelle clé n'y est pas renseignée.
        </p>
      )}
    </div>
  );
};

export default ApiKeysSection;
